// npm i papaparse jszip
import Papa from "papaparse";
import JSZip from "jszip";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

/* ─────────── Helpers ─────────── */

// Timestamp: YYYY-MM-DD_HH-MM-SS
export function getTimestamp(addTimeDetails = false) {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  if (addTimeDetails) {
    return `${y}-${m}-${day}-${h}-${min}`
  } else {
    return `${y}-${m}-${day}`;
  }
}

// (1) Accept array or JSON string; safely parse if needed
function ensureData(tbd) {
  if (!tbd) return [];
  if (typeof tbd === "string") {
    try { return JSON.parse(tbd); } catch { return []; }
  }
  return tbd;
}

// (2) Normalize a month/quarter entry that might be an object or an array of objects
function normalizePeriodEntry(entry) {
  if (!entry) return null;
  if (Array.isArray(entry)) {
    const merged = Object.assign(
      {},
      ...entry.filter(e => e && typeof e === "object" && !Array.isArray(e))
    );
    return Object.keys(merged).length ? merged : null;
  }
  return (typeof entry === "object") ? entry : null;
}

// CSV builder; adds BOM (3) for better Excel compatibility
function buildCSV(rows) {
  const csv = (!rows || rows.length === 0)
    ? Papa.unparse([{
      "Period Type": "", "Period Label": "", "State": "", "Tax Type": "", "Amount": ""
    }], { header: true })
    : Papa.unparse(rows, { header: true, quotes: false, delimiter: ",", newline: "\r\n" });

  // (3) Include BOM
  return "\uFEFF" + csv;
}

// (4) Robust file download (handles Safari/iOS better)
function triggerDownload(contentBlob, filename) {
  const url = URL.createObjectURL(contentBlob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke in next tick to avoid some browser oddities
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/* ─────────── Flatteners ─────────── */

export function flattenMonthly(tickets_tax_breakdown) {
  const data = ensureData(tickets_tax_breakdown);
  const monthly = data?.[0] || [];
  const rows = [];

  monthly.forEach((rawEntry, monthIdx) => {
    const periodObj = normalizePeriodEntry(rawEntry);
    if (!periodObj) return;

    Object.entries(periodObj).forEach(([state, taxes]) => {
      if (!taxes || typeof taxes !== "object") return;
      Object.entries(taxes).forEach(([taxType, amount]) => {
        rows.push({
          "Period Type": "Monthly",
          "Period Label": MONTHS[monthIdx] ?? `M${monthIdx + 1}`,
          "State": state,
          "Tax Type": taxType,
          "Amount": Number(amount)
        });
      });
    });
  });

  return rows;
}

export function flattenQuarterly(tickets_tax_breakdown) {
  const data = ensureData(tickets_tax_breakdown);
  const quarterly = data?.[1] || [];
  const rows = [];

  quarterly.forEach((rawEntry, qIdx) => {
    const periodObj = normalizePeriodEntry(rawEntry);
    if (!periodObj) return;

    Object.entries(periodObj).forEach(([state, taxes]) => {
      if (!taxes || typeof taxes !== "object") return;
      Object.entries(taxes).forEach(([taxType, amount]) => {
        rows.push({
          "Period Type": "Quarterly",
          "Period Label": QUARTERS[qIdx] ?? `Q${qIdx + 1}`,
          "State": state,
          "Tax Type": taxType,
          "Amount": Number(amount)
        });
      });
    });
  });

  return rows;
}

/* ─────────── Individual CSV Downloads ─────────── */

export function downloadTaxMonthlyCSV(tickets_tax_breakdown, year) {
  const ts = getTimestamp();
  const rows = flattenMonthly(tickets_tax_breakdown);
  const csv = buildCSV(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, `${year}_LiveNyte_Tax_Breakdown_Monthly (${ts}).csv`);
}

export function downloadTaxQuarterlyCSV(tickets_tax_breakdown, year) {
  const ts = getTimestamp();
  const rows = flattenQuarterly(tickets_tax_breakdown);
  const csv = buildCSV(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, `${year}_LiveNyte_Tax_Breakdown_Quarterly (${ts}).csv`);
}

/* ─────────── ZIP Download (Monthly + Quarterly) ─────────── */

export async function downloadTaxCSVsZip(tickets_tax_breakdown, year) {
  const ts = getTimestamp();

  const monthlyCSV = buildCSV(flattenMonthly(tickets_tax_breakdown));
  const quarterlyCSV = buildCSV(flattenQuarterly(tickets_tax_breakdown));

  const zip = new JSZip();
  zip.file(`${year}_LiveNyte_Tax_Breakdown_Monthly (${ts}).csv`, monthlyCSV);
  zip.file(`${year}_LiveNyte_Tax_Breakdown_Quarterly (${ts}).csv`, quarterlyCSV);

  const blob = await zip.generateAsync({ type: "blob" });
  triggerDownload(blob, `${year}_LiveNyte_Tax_Breakdown (${ts}).zip`);
}